/**
 * Logger Utility
 * Environment-aware logging that suppresses console output in production
 * and provides a central place to integrate with monitoring services
 */

type LogLevel = 'error' | 'warn' | 'info' | 'debug';

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: unknown;
  timestamp: Date;
}

class Logger {
  private isDevelopment: boolean;
  private logs: LogEntry[] = [];
  private maxLogs = 100; // Keep last 100 logs in memory

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
  }

  /**
   * Log an error message
   * In production, this would send to monitoring service (Sentry, DataDog, etc.)
   */
  error(message: string, context?: unknown): void {
    this.log('error', message, context);

    if (this.isDevelopment) {
      console.error(`[ERROR] ${message}`, context || '');
    } else {
      // In production, send to monitoring service
      // Example: Sentry.captureException(new Error(message), { extra: context });
    }
  }

  /**
   * Log a warning message
   */
  warn(message: string, context?: unknown): void {
    this.log('warn', message, context);

    if (this.isDevelopment) {
      console.warn(`[WARN] ${message}`, context || '');
    }
  }

  /**
   * Log an info message
   */
  info(message: string, context?: unknown): void {
    this.log('info', message, context);

    if (this.isDevelopment) {
      console.info(`[INFO] ${message}`, context || '');
    }
  }

  /**
   * Log a debug message (only in development)
   */
  debug(message: string, context?: unknown): void {
    if (this.isDevelopment) {
      this.log('debug', message, context);
      console.debug(`[DEBUG] ${message}`, context || '');
    }
  }

  /**
   * Store log entry in memory (for debugging or export)
   */
  private log(level: LogLevel, message: string, context?: unknown): void {
    const entry: LogEntry = {
      level,
      message,
      context,
      timestamp: new Date(),
    };

    this.logs.push(entry);

    // Keep only last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }
  }

  /**
   * Get recent logs (useful for debugging or error reports)
   */
  getRecentLogs(): LogEntry[] {
    return [...this.logs];
  }

  /**
   * Clear stored logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Export logs as JSON (useful for bug reports)
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for testing
export { Logger, type LogEntry, type LogLevel };

